import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, MessageSquare, Heart, Share, Users, Eye } from "lucide-react";
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

interface EngagementAnalyticsProps {
  isConnected: boolean;
}

export const EngagementAnalytics = ({ isConnected }: EngagementAnalyticsProps) => {
  // Mock data for charts
  const engagementData = [
    { date: '2024-01-01', likes: 45, comments: 12, shares: 8, views: 234 },
    { date: '2024-01-02', likes: 52, comments: 18, shares: 15, views: 298 },
    { date: '2024-01-03', likes: 38, comments: 9, shares: 6, views: 187 },
    { date: '2024-01-04', likes: 67, comments: 24, shares: 19, views: 356 },
    { date: '2024-01-05', likes: 78, comments: 31, shares: 23, views: 412 },
    { date: '2024-01-06', likes: 56, comments: 16, shares: 11, views: 289 },
    { date: '2024-01-07', likes: 89, comments: 42, shares: 28, views: 534 },
  ];

  const postPerformance = [
    { title: "Future of Recruiting", likes: 89, comments: 42, shares: 28, engagement: 15.8 },
    { title: "AI in HR Technology", likes: 67, comments: 24, shares: 19, engagement: 12.3 },
    { title: "Remote Work Trends", likes: 78, comments: 31, shares: 23, engagement: 14.2 },
    { title: "Talent Acquisition Tips", likes: 52, comments: 18, shares: 15, engagement: 9.8 },
  ];

  const audienceInsights = [
    { name: 'HR Professionals', value: 35, color: '#3b82f6' },
    { name: 'Recruiters', value: 28, color: '#8b5cf6' },
    { name: 'Tech Workers', value: 20, color: '#10b981' },
    { name: 'Business Leaders', value: 17, color: '#f59e0b' },
  ];

  const metrics = [
    {
      title: "Total Reach",
      value: "12,847",
      change: "+18.2%",
      trend: "up",
      icon: Eye,
      description: "People who saw your content"
    },
    {
      title: "Engagement Rate",
      value: "4.8%",
      change: "+0.9%",
      trend: "up",
      icon: Heart,
      description: "Likes, comments, shares per view"
    },
    {
      title: "New Followers",
      value: "156",
      change: "+23.1%",
      trend: "up",
      icon: Users,
      description: "New profile followers this week"
    },
    {
      title: "Post Frequency",
      value: "3.2/week",
      change: "-0.8",
      trend: "down",
      icon: MessageSquare,
      description: "Average posts per week"
    }
  ];

  if (!isConnected) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="flex flex-col items-center gap-4">
            <TrendingUp className="h-12 w-12 text-muted-foreground" />
            <div>
              <h3 className="text-lg font-semibold">Connect LinkedIn First</h3>
              <p className="text-muted-foreground">Connect your LinkedIn account to view analytics</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Engagement Analytics</h2>
          <p className="text-muted-foreground">Track your LinkedIn performance and insights</p>
        </div>
        <Select defaultValue="7d">
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
            <SelectItem value="1y">Last year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric, index) => {
          const IconComponent = metric.icon;
          return (
            <Card key={index}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{metric.title}</p>
                    <p className="text-2xl font-bold">{metric.value}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {metric.trend === "up" ? (
                        <TrendingUp className="h-3 w-3 text-green-600" />
                      ) : (
                        <TrendingDown className="h-3 w-3 text-red-600" />
                      )}
                      <span className={`text-xs ${metric.trend === "up" ? "text-green-600" : "text-red-600"}`}>
                        {metric.change}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{metric.description}</p>
                  </div>
                  <IconComponent className="h-8 w-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Engagement Trend */}
        <Card>
          <CardHeader>
            <CardTitle>Engagement Trend</CardTitle>
            <CardDescription>Daily engagement metrics over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={engagementData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  className="text-xs"
                />
                <YAxis className="text-xs" />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--background))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px'
                  }}
                />
                <Line type="monotone" dataKey="likes" stroke="#3b82f6" strokeWidth={2} name="Likes" />
                <Line type="monotone" dataKey="comments" stroke="#8b5cf6" strokeWidth={2} name="Comments" />
                <Line type="monotone" dataKey="shares" stroke="#10b981" strokeWidth={2} name="Shares" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Audience Insights */}
        <Card>
          <CardHeader>
            <CardTitle>Audience Breakdown</CardTitle>
            <CardDescription>Who's engaging with your content</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={audienceInsights}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) => `${name} ${value}%`}
                >
                  {audienceInsights.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Percentage']}
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--background))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Post Performance */}
      <Card>
        <CardHeader>
          <CardTitle>Top Performing Posts</CardTitle>
          <CardDescription>Your best content from the last 30 days</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {postPerformance.map((post, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium">{post.title}</h4>
                  <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Heart className="h-3 w-3" />
                      {post.likes}
                    </div>
                    <div className="flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" />
                      {post.comments}
                    </div>
                    <div className="flex items-center gap-1">
                      <Share className="h-3 w-3" />
                      {post.shares}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-green-600">{post.engagement}%</p>
                  <p className="text-xs text-muted-foreground">Engagement Rate</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Weekly Overview Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Weekly Overview</CardTitle>
          <CardDescription>Content reach and engagement by day</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={engagementData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { weekday: 'short' })}
                className="text-xs"
              />
              <YAxis className="text-xs" />
              <Tooltip 
                labelFormatter={(value) => new Date(value).toLocaleDateString()}
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--background))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="views" 
                stackId="1" 
                stroke="#3b82f6" 
                fill="#3b82f6" 
                fillOpacity={0.3}
                name="Views"
              />
              <Area 
                type="monotone" 
                dataKey="likes" 
                stackId="2" 
                stroke="#10b981" 
                fill="#10b981" 
                fillOpacity={0.6}
                name="Likes"
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};